import { Slot } from '@radix-ui/react-slot';
import { twMerge } from '@tuic/tailwind-config';

import type { FC, SVGProps } from 'react';

import './style.css';

// TODO replace with core component: https://www.figma.com/design/m3EQFZPt7F6hbwxxOx3fnC/TUIC-Foundation?node-id=2433-4738&t=uHGmAMnYfr7xjDOW-0
// Talk to ECO about naming of Ship-Indicator

export type AnimatedShipIndicatorProps = {
  on: 'white' | 'gray';
  className?: string;
  asChild?: boolean;
  // Die CSS Animation ist schneller verfügbar als die SMIL-Animation
  useCssAnimation?: boolean;
};

export const AnimatedShipIndicator: FC<AnimatedShipIndicatorProps> = ({
  asChild,
  on,
  className,
  useCssAnimation = false,
  ...props
}) => {
  const Component = asChild ? Slot : 'div';

  return (
    <Component
      className={twMerge(
        'size-[100px] md:size-[140px]',
        className,
        'overflow-hidden rounded-full',
        on === 'white' && 'text-[#f0f1f5]',
        on === 'gray' && 'text-white',
      )}
      {...props}
    >
      <AnimatedShip
        className="aspect-square size-full"
        useCssAnimation={useCssAnimation}
      />
    </Component>
  );
};

type AnimatedShipProps = SVGProps<SVGSVGElement> & {
  useCssAnimation?: boolean;
};

const AnimatedShip: FC<AnimatedShipProps> = ({
  useCssAnimation = false,
  ...props
}) => {
  const shipClassName = useCssAnimation ? 'animated-ship__ship' : undefined;
  const wavesClassName = useCssAnimation ? 'animated-ship__waves' : undefined;

  return (
    <svg viewBox="10 10 230 230" xmlns="http://www.w3.org/2000/svg" {...props}>
      <rect x="0" y="0" width="250" height="250" fill="currentColor" />
      <g className={shipClassName} transform="translate(58, 37)">
        <path
          fill="#263e6a"
          d="M2.8 96L19.1 139.9L22.8 138.5L10.1 104.3L64.5 85.8V103.4C64.5 104.5 65.4 105.4 66.5 105.4C67.6 105.4 68.5 104.5 68.5 103.4V85.6L122.9 104.8L109.4 140.9L113.1 142.3L130.5 95.8C132 91.7 129.9 87.1 125.8 85.6L119.9 83.5V50.4H128.9C130 50.4 130.9 49.5 130.9 48.4V36.5C130.9 35.4 130 34.5 128.9 34.5H113.7V18.6C113.7 16.4 111.9 14.6 109.7 14.6H79.2V4.8C79.2 2.6 77.4 0.800003 75.2 0.800003H57.7C55.5 0.800003 53.7 2.6 53.7 4.8V14.6H23.1C20.9 14.6 19.1 16.4 19.1 18.6V34.5H4C2.9 34.5 2 35.4 2 36.5V48.4C2 49.5 2.9 50.4 4 50.4H13V83.8L7.5 85.8M7.5 85.8C3.4 87.3 1.2 91.9 2.8 96L7.5 85.8ZM6 38.5H126.8V46.4H115.8H17H6V38.5ZM57.7 4.8H75.1V14.6H57.7V4.8ZM23.2 18.6H109.7V34.5H23.2V18.6ZM73 66.7C71.2 66.1 68.9 65.8 66.7 65.8C64.5 65.8 62.2 66.1 60.4 66.7M60.4 66.7L17 82.3V50.3H115.8V81.9L73 66.6M8.7 100.6L6.5 94.6C5.7 92.6 6.8 90.3 8.9 89.5L61.8 70.5C62.6 70.2 63.5 70 64.6 69.9V81.6L8.7 100.6ZM126.7 94.4L124.2 101L68.5 81.4V69.9C69.7 70 70.8 70.2 71.7 70.5L124.4 89.3C126.4 90 127.5 92.3 126.7 94.3"
        />
        {!useCssAnimation && (
          <animateTransform
            attributeName="transform"
            type="translate"
            values="58, 57; 58, 48; 58, 57;"
            dur="1s"
            repeatCount="indefinite"
          />
        )}
      </g>
      <rect x="0" y="192" width="250" height="58" fill="currentColor" />
      <g className={wavesClassName} transform="translate(33, 173)">
        <path
          fill="#bee3ee"
          d="M398.1 3.49999C380.9 3.49999 370 8.19999 360.7 13.2C359.7 13.7 358.7 14.3 357.7 14.9C351.5 18.4 347 20.9 340.7 20.9C334.4 20.9 331.2 18.8 325.9 15.4C324.9 14.8 323.9 14.1 322.9 13.4C315.6 8.79999 306.6 4.59999 290.9 3.59999V3.39999H282.5C265.3 3.39999 254.4 8.09999 245.1 13.1C244.1 13.7 243.1 14.2 242.1 14.8C235.9 18.3 231.4 20.8 225.1 20.8C218.8 20.8 215.6 18.7 210.3 15.3H210.1C209.2 14.6 208.3 14 207.3 13.3C200 8.69999 191 4.39999 175.2 3.49999V3.29999H168.9C168.8 3.29999 168.7 3.29999 168.6 3.29999H160.2V3.59999C146.7 4.59999 137.4 8.69999 129.4 13.1C128.4 13.6 127.4 14.2 126.4 14.8C120.2 18.3 115.7 20.8 109.4 20.8C103.1 20.8 99.9 18.7 94.6 15.3H94.5C93.6 14.6 92.6 14 91.6 13.3C84.3 8.69999 75.3 4.49999 59.6 3.49999V3.29999H51.2C34 3.29999 23.1 7.99999 13.8 13C12.8 13.6 11.8 14.1 10.8 14.7C4.60001 18.2 0.100006 20.7 -6.2 20.7C-12.5 20.7 -15.7 18.6 -21 15.2C-21.9 14.5 -22.9 13.9 -23.9 13.2C-31.7 8.29999 -42.1 3.49999 -62.1 3.49999H-70.5V20.2H-62.1C-45.5 20.2 -38 24 -32.2 27.6C-31.3 28.2 -30.4 28.8 -29.5 29.4C-23.6 33.3 -16.9 37.6 -5.6 37.6C5.7 37.6 12.4 33.4 19.6 29.4C20.5 28.9 21.5 28.4 22.4 27.8C29.3 24 37.3 20.3 50.8 20.1H53.3C69.9 20.1 77.4 23.9 83.2 27.5C84.1 28.1 85 28.7 85.9 29.3C91.8 33.2 98.5 37.5 109.8 37.5C121.1 37.5 127.8 33.3 135 29.3C135.9 28.7 136.8 28.2 137.7 27.7C144.8 23.8 152.9 20 167 20H169.1C185.4 20 192.9 23.9 198.6 27.4C199.5 28 200.4 28.6 201.3 29.2C207.2 33.1 213.9 37.4 225.2 37.4C236.5 37.4 243.2 33.2 250.4 29.2C251.3 28.7 252.2 28.2 253.1 27.7C260 23.9 268 20.2 281.6 20H284.1C300.7 20 308.2 23.8 314 27.4C314.9 28 315.8 28.5 316.6 29.1H316.7C322.6 33 329.3 37.4 340.6 37.4C351.9 37.4 358.6 33.2 365.8 29.2C366.7 28.7 367.7 28.2 368.6 27.6C375.7 23.7 383.8 19.9 397.9 19.9H406.3V3.49999H397.9H398.1Z"
        />
        <path
          fill="#263e6a"
          d="M398.5 0.999994C381.9 0.999994 372.1 5.59999 364.2 9.89999C363.2 10.4 362.2 11 361.3 11.5C354.7 15.2 349.1 18.4 341.1 18.4C333.1 18.4 328.4 15.5 322.8 11.9C321.9 11.3 320.9 10.6 319.9 9.99999C312.5 5.29999 303.1 0.999994 284.8 0.999994H282.9C266.3 0.999994 256.5 5.59999 248.6 9.89999C247.6 10.4 246.7 11 245.8 11.5C239.2 15.2 233.6 18.4 225.6 18.4C217.6 18.4 212.9 15.5 207.2 11.8C206.3 11.2 205.3 10.6 204.3 9.89999C196.8 5.19999 187.4 0.899994 169 0.899994H167.1C150.8 0.999994 141.1 5.59999 133.3 9.79999C132.3 10.3 131.3 10.9 130.4 11.4C123.8 15.1 118.2 18.3 110.2 18.3C102.2 18.3 97.5 15.4 91.9 11.8C91 11.2 90 10.6 89 9.89999C81.6 5.19999 72.2 0.899994 53.9 0.899994H52C35.4 0.899994 25.6 5.49999 17.7 9.79999C16.7 10.3 15.7 10.9 14.8 11.4C8.2 15.1 2.6 18.3 -5.4 18.3C-13.4 18.3 -18.1 15.4 -23.8 11.7C-24.7 11.1 -25.7 10.5 -26.7 9.79999C-34.3 5.29999 -43.7 0.999994 -62.1 0.999994H-64V4.69999H-62.1C-43.7 4.69999 -35.3 9.09999 -28.7 13.1C-27.7 13.7 -26.8 14.3 -25.9 14.9C-20.3 18.6 -14.9 22.1 -5.5 22.1C3.9 22.1 9.8 18.5 16.5 14.8H16.6C17.5 14.2 18.4 13.7 19.4 13.2C26.9 9.09999 36.1 4.79999 51.7 4.69999H53.6C72 4.69999 80.4 9.09999 87 13.1C88 13.7 88.9 14.3 89.8 14.9C95.4 18.6 100.8 22.1 110.2 22.1C119.6 22.1 125.5 18.5 132.2 14.8C133.1 14.3 134.1 13.7 135.1 13.2C142.6 9.09999 151.9 4.69999 167.6 4.69999H169.3C187.4 4.69999 195.8 9.09999 202.3 13.1C203.3 13.7 204.2 14.3 205.1 14.9C210.7 18.6 216.1 22.1 225.5 22.1C234.9 22.1 240.8 18.5 247.5 14.8C248.4 14.3 249.4 13.7 250.4 13.2C257.9 9.09999 267.1 4.79999 282.7 4.69999H284.6C303 4.69999 311.4 9.09999 318 13.1C319 13.7 319.9 14.3 320.8 14.9C326.4 18.6 331.8 22.1 341.2 22.1C350.6 22.1 356.4 18.6 363.1 14.8C364 14.2 365 13.7 366 13.2C373.5 9.09999 382.8 4.69999 398.5 4.69999H400.4V0.999994H398.5Z"
        />
        {!useCssAnimation && (
          <animateTransform
            attributeName="transform"
            type="translate"
            from="65, 173"
            to="-50, 173"
            dur="1s"
            repeatCount="indefinite"
          />
        )}
      </g>
    </svg>
  );
};
